import axios, { AxiosResponse } from 'axios';
import logger from './logger';

interface NetworkSession {
  startTime: number;
  messageCount: number;
  totalBytes: number;
  url: string;
}

const eventSourceSessions = new Map<EventSource, NetworkSession>();

const isLoggerRequest = (url?: any): boolean => {
  return url?.includes('/logger/') || false;
};

export const initializeNetworkLogging = () => {
  setupAxiosInterceptors();
  patchFetch();
  patchEventSource();
};

const setupAxiosInterceptors = () => {
  axios.interceptors.request.use(
    (config) => {
      if (isLoggerRequest(config.url)) {
        return config;
      }

      const startTime = Date.now();
      (config as any).__startTime = startTime;

      logger.log({
        event_type: 'network_request_full',
        api_endpoint: config.url,
        request_method: config.method?.toUpperCase(),
        request_headers: sanitizeHeaders(config.headers),
        request_body: sanitizeBody(config.data),
        base_url: config.baseURL,
        full_url: buildFullUrl(config),
        timestamp: startTime
      });

      return config;
    },
    (error) => {
      logger.error({
        event_type: 'network_request_error',
        error: error,
        message: 'Axios request failed'
      });
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      if (isLoggerRequest(response.config.url)) {
        return response;
      }

      const endTime = Date.now();
      const startTime = (response.config as any).__startTime || endTime;

      logger.log({
        event_type: 'network_response_full',
        api_endpoint: response.config.url,
        request_method: response.config.method?.toUpperCase(),
        response_status: response.status,
        response_headers: sanitizeHeaders(response.headers),
        response_body: sanitizeResponseBody(response.data),
        api_response_time: endTime - startTime,
        full_url: buildFullUrl(response.config),
        timestamp: endTime
      });

      return response;
    },
    (error) => {
      if (isLoggerRequest(error.config?.url)) {
        return Promise.reject(error);
      }

      const endTime = Date.now();
      const startTime = (error.config as any)?.__startTime || endTime;

      logger.error({
        event_type: 'network_response_error',
        api_endpoint: error.config?.url,
        request_method: error.config?.method?.toUpperCase(),
        response_status: error.response?.status,
        response_headers: sanitizeHeaders(error.response?.headers),
        api_response_time: endTime - startTime,
        full_url: buildFullUrl(error.config),
        error: error,
        message: 'Axios response failed'
      });

      return Promise.reject(error);
    }
  );
};

const patchFetch = () => {
  const originalFetch = window.fetch;

  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const url = args[0] instanceof Request ? args[0].url : args[0];

    if (isLoggerRequest(url)) {
      return originalFetch(...args);
    }

    const startTime = Date.now();
    const method = args[1]?.method || (args[0] instanceof Request ? args[0].method : 'GET');

    const headers = await extractFetchHeaders(args);
    const body = await extractFetchBody(args);

    logger.log({
      event_type: 'network_request_full',
      api_endpoint: typeof url === "string" ? url : url.toString(),
      request_method: method.toUpperCase(),
      request_headers: sanitizeHeaders(headers),
      request_body: sanitizeBody(body),
      full_url: typeof url === "string" ? url : url.toString(),
      timestamp: startTime
    });

    try {
      const response = await originalFetch(...args);
      const endTime = Date.now();

      const responseHeaders = extractResponseHeaders(response);
      
      // Skip body extraction for streaming responses (SSE/EventSource)
      const contentType = response.headers.get('content-type') || '';
      const isStreamingResponse = contentType.includes('text/event-stream');
      
      let responseBody = null;
      if (!isStreamingResponse) {
        responseBody = await extractResponseBody(response.clone());
      }

      logger.log({
        event_type: 'network_response_full',
        api_endpoint: typeof url === "string" ? url : url.toString(),
        request_method: method.toUpperCase(),
        response_status: response.status,
        response_headers: sanitizeHeaders(responseHeaders),
        response_body: isStreamingResponse ? '[STREAMING RESPONSE - BODY NOT LOGGED]' : sanitizeResponseBody(responseBody),
        api_response_time: endTime - startTime,
        full_url: typeof url === "string" ? url : url.toString(),
        timestamp: endTime
      });

      return response;
    } catch (error) {
      const endTime = Date.now();

      logger.error({
        event_type: 'network_response_error',
        api_endpoint: typeof url === "string" ? url : url.toString(),
        request_method: method.toUpperCase(),
        api_response_time: endTime - startTime,
        full_url: typeof url === "string" ? url : url.toString(),
        error: error,
        message: 'Fetch request failed'
      });

      throw error;
    }
  };
};

const patchEventSource = () => {
  const OriginalEventSource = window.EventSource;

  window.EventSource = class extends OriginalEventSource {
    constructor(url: string | URL, eventSourceInitDict?: EventSourceInit) {
      super(url, eventSourceInitDict);

      const session: NetworkSession = {
        startTime: Date.now(),
        messageCount: 0,
        totalBytes: 0,
        url: url.toString()
      };

      eventSourceSessions.set(this, session);

      logger.log({
        event_type: 'eventsource_open',
        api_endpoint: url.toString(),
        request_method: 'GET',
        full_url: typeof url === "string" ? url : url.toString(),
        timestamp: session.startTime
      });

      const originalOnMessage = this.onmessage;
      this.onmessage = (event) => {
        const session = eventSourceSessions.get(this);
        if (session) {
          session.messageCount++;
          session.totalBytes += event.data.length;

          if (event.data === '[DONE]' ||
            event.data.startsWith('[ERROR]') ||
            event.data.startsWith('[WARNING]') ||
            event.data.startsWith('[EMPTY_INDEX]')) {
            logger.log({
              event_type: 'eventsource_control',
              api_endpoint: session.url,
              event_detail: event.data,
              message_count: session.messageCount,
              total_data_size: session.totalBytes
            });
          }
        }

        if (originalOnMessage) {
          originalOnMessage.call(this, event);
        }
      };

      const originalOnError = this.onerror;
      this.onerror = (event) => {
        const session = eventSourceSessions.get(this);
        if (session) {
          logger.error({
            event_type: 'eventsource_error',
            api_endpoint: session.url,
            message_count: session.messageCount,
            total_data_size: session.totalBytes,
            api_response_time: Date.now() - session.startTime,
            error: event
          });
          eventSourceSessions.delete(this);
        }

        if (originalOnError) {
          originalOnError.call(this, event);
        }
      };

      const originalClose = this.close;
      this.close = () => {
        const session = eventSourceSessions.get(this);
        if (session) {
          logger.log({
            event_type: 'eventsource_close',
            api_endpoint: session.url,
            message_count: session.messageCount,
            total_data_size: session.totalBytes,
            api_response_time: Date.now() - session.startTime
          });
          eventSourceSessions.delete(this);
        }

        originalClose.call(this);
      };
    }
  };
};

const buildFullUrl = (config: any): string => {
  if (!config) return '';
  const baseURL = config.baseURL || '';
  const url = config.url || '';
  return `${baseURL}${url}`;
};

const sanitizeHeaders = (headers: any): Record<string, string> => {
  if (!headers) return {};

  const sanitized: Record<string, string> = {};

  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      sanitized[key] = sanitizeHeaderValue(key, value);
    });
  } else if (typeof headers === 'object') {
    Object.entries(headers).forEach(([key, value]) => {
      sanitized[key] = sanitizeHeaderValue(key, String(value));
    });
  }

  return sanitized;
};

const sanitizeHeaderValue = (key: string, value: string): string => {
  const lowerKey = key.toLowerCase();
  if (lowerKey === 'authorization' || lowerKey === 'cookie') {
    return '[REDACTED]';
  }
  return value;
};

const sanitizeBody = (body: any): any => {
  if (!body) return null;

  const bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
  if (bodyStr.length > 10000) {
    return `[TRUNCATED - ${bodyStr.length} chars]: ${bodyStr.substring(0, 1000)}...`;
  }

  return body;
};

const sanitizeResponseBody = (body: any): any => {
  if (!body) return null;

  const bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
  if (bodyStr.length > 5000) {
    return `[TRUNCATED - ${bodyStr.length} chars]: ${bodyStr.substring(0, 500)}...`;
  }

  return body;
};

const extractFetchHeaders = async (args: Parameters<typeof fetch>): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {};

  if (args[0] instanceof Request) {
    args[0].headers.forEach((value, key) => {
      headers[key] = value;
    });
  } else if (args[1]?.headers) {
    if (args[1].headers instanceof Headers) {
      args[1].headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (typeof args[1].headers === 'object') {
      Object.entries(args[1].headers).forEach(([key, value]) => {
        headers[key] = String(value);
      });
    }
  }

  return headers;
};

const extractFetchBody = async (args: Parameters<typeof fetch>): Promise<any> => {
  if (args[0] instanceof Request) {
    try {
      return await args[0].clone().text();
    } catch {
      return null;
    }
  }
  return args[1]?.body || null;
};

const extractResponseHeaders = (response: Response): Record<string, string> => {
  const headers: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headers[key] = value;
  });
  return headers;
};

const extractResponseBody = async (response: Response): Promise<any> => {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return await response.json();
    } else {
      return await response.text();
    }
  } catch {
    return null;
  }
};

